import { EM_DASH } from "constants/index";

import React from "react";

import { CalendarToday } from "@material-ui/icons";
import { Button, Paper, portexColor, TextInput } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import StepCollapse from "app/pages/quote-request/_components/StepCollapse";
import TemplateOption from "app/pages/quote-request/_components/TemplateOption";
import { useCreateStepCollapseProps } from "app/pages/quote-request/_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { QuoteStepIndex } from "app/pages/quote-request/types";
import { freightClassDictionary } from "app/pages/shipments/components/EditableShipmentPackageGroupElements";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import AttachFileFromButtonContainer from "components/file-uploads/AttachFileFromButtonContainer";
import AttachmentsListContainer from "components/file-uploads/AttachmentsListContainer";
import RecipientTags from "components/RecipientTags";
import Text from "components/Text";
import useLDFlag from "hooks/useLDFlag";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import { DateTime } from "luxon";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TrailerTypeEnum } from "types/TrailerType";

import { FinalSchema } from "../../_schemas";

const getTrailerTypeCopy = (trailerType: TrailerTypeEnum) => {
  switch (trailerType) {
    case TrailerTypeEnum.RGN:
      return "Removable Gooseneck (RGN)";
    case TrailerTypeEnum.NA_PARTNERS_CHOICE:
      return "N/A - Partner's Choice";
    default: {
      return startCase(capitalize(trailerType));
    }
  }
};

export const ReviewStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "quoteRequest", "shipper"]);
  const { baseStepCollapseProps } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Review,
    name: t("shipper:reviewAndSend"),
    number: 5,
  });
  const releaseQuoteRequestFiles = useLDFlag("releaseQuoteRequestFiles");
  const { quoteRequest: quoteRequest } = useQuoteRequestSelector((state) => {
    return state.quoteRequestSliceV2;
  });

  const { data: partnerData } = useGetPartnersRequestQuery(
    !!quoteRequest.partners?.length
      ? {
          queryParams: {
            partnerType: "external",
            ids: quoteRequest.partners?.map((partner) => partner.contact_id) ?? [],
          },
        }
      : skipToken
  );
  const { data: teammatesData } = useGetPartnersRequestQuery(
    !!quoteRequest.teammates?.length
      ? {
          queryParams: {
            partnerType: "internal",
            ids: quoteRequest.teammates?.map((teammate) => teammate.contact_id) ?? [],
          },
        }
      : skipToken
  );

  const methods = useFormContext<FinalSchema>();

  const submit = (data: FinalSchema) => {
    onSubmit(data);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      footer={
        <div className="flex justify-between items-center mt-2 w-full">
          <div className="flex items-center gap-4"></div>
          <div className="flex justify-between items-center space-x-3">
            <TemplateOption />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 200 }}
            >
              {t("quoteRequest:ReviewStep.sendQuote")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col w-full">
        <div className="mb-4">
          <p className="text-sm text-gray-600">{t("quoteRequest:ReviewStep.subtitle")}</p>
        </div>

        <div className="flex flex-col w-full mt-6">
          {/* Email Preview Section */}
          <div className="mb-6">
            <Paper variant="outlined" className="bg-gray-50 p-4">
              <div className="flex flex-col space-y-3 mb-4">
                <div className="flex items-center">
                  <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.bcc")}:</span>
                  <RecipientTags
                    recipients={
                      partnerData?.data.partners.map((partner) => partner.company_name ?? partner.user.email) ?? []
                    }
                    limit={3}
                    customStyle={{ backgroundColor: "#F3F4F5" }}
                  />
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.cc")}:</span>
                  <RecipientTags
                    recipients={teammatesData?.data.partners.map((teammate) => teammate.user.email) ?? []}
                    limit={3}
                    customStyle={{ backgroundColor: "#F3F4F5" }}
                  />
                </div>
                <div className="flex items-center mb-4">
                  <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.subject")}:</span>
                  <TextInput value={quoteRequest.email_metadata?.subject} fullWidth className="bg-white" />
                </div>
              </div>

              {!!releaseQuoteRequestFiles && (
                <div className="flex pb-4 justify-end">
                  <AttachFileFromButtonContainer />
                </div>
              )}

              <Paper variant="outlined">
                <div className="flex justify-between p-4 items-center border-b-2">
                  <div className="flex flex-col">
                    <Text size="large" weight="bold">
                      {t("common:quoteRequest")}
                    </Text>
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {quoteRequest.portex_id}
                    </Text>
                  </div>

                  {quoteRequest.deadline_respond_at ? (
                    <div className="bg-orange-100 text-red-500 py-2 px-2 rounded-[8px]">
                      {t("common:deadlineIn{{time}}", {
                        time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative(),
                      })}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>

                <div className="flex">
                  <div className="flex flex-col w-1/2 border-r-2 p-4 space-y-4">
                    <Text size="small" weight="bold">
                      {t("shipper:shipmentDetails")}
                    </Text>

                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:mode")}
                      </Text>
                      <Text size="small" weight="bold">
                        {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                      </Text>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:equipment")}
                      </Text>
                      <Text size="small" weight="bold">
                        {quoteRequest.load_spec?.trailer_type
                          ? getTrailerTypeCopy(quoteRequest.load_spec.trailer_type as TrailerTypeEnum)
                          : EM_DASH}
                      </Text>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:shipper")}
                      </Text>
                      <Text size="small" weight="bold">
                        {quoteRequest.shipper_name ? quoteRequest.shipper_name : EM_DASH}
                      </Text>
                    </div>

                    <div className="flex flex-col space-y-2">
                      <Text size="small" weight="bold">
                        {t("common:cargoDetails")}
                      </Text>
                      <table>
                        <thead className="bg-grey-50 border-b-2 p-2">
                          <tr>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:item")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("shipper:dimensions")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:commodities")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:totalWeight")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("shipper:packageGroupView.freightClass")}
                              </Text>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {quoteRequest.load_spec?.package_groups?.map((packageGroup) => (
                            <tr>
                              <td className="p-2">
                                <Text size="small" weight="bold">
                                  {`${packageGroup.item_quantity} x ${capitalize(
                                    packageGroup.packaging_type
                                  ).replaceAll(/_/g, " x ")}`}
                                </Text>
                              </td>
                              <td className="p-2">
                                <Text size="small" weight="bold">
                                  {`${packageGroup.width_per_package} x ${packageGroup.length_per_package} x ${
                                    packageGroup.height_per_package
                                  } ${t("common:in")}`}
                                </Text>
                              </td>
                              <td className="p-2">
                                <Text size="small" weight="bold">
                                  {packageGroup.commodities?.split(",").join(", ")}
                                </Text>
                              </td>
                              <td className="p-2">
                                <Text size="small" weight="bold">
                                  {t("common:{{value}}lb", {
                                    value: (packageGroup.weight_per_package ?? 1) * (packageGroup.item_quantity ?? 1),
                                  })}
                                </Text>
                              </td>
                              <td className="p-2">
                                <Text size="small" weight="bold">
                                  {packageGroup.freight_class
                                    ? freightClassDictionary[packageGroup.freight_class]
                                    : EM_DASH}
                                </Text>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:commodities")}
                      </Text>
                      <Text size="small" weight="bold">
                        {quoteRequest.load_spec?.commodities
                          ? quoteRequest.load_spec.commodities.split(",").join(", ")
                          : EM_DASH}
                      </Text>
                    </div>

                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:attachments")}
                      </Text>
                      <Text size="small" weight="bold">
                        <AttachmentsListContainer showDashOnNoAttachments />
                      </Text>
                    </div>
                  </div>

                  <div className="flex flex-col w-1/2 p-4 space-y-3">
                    <Text size="small" weight="bold">
                      {t("shipper:locations")}
                    </Text>

                    <div className="flex flex-col space-y-3">
                      <Text size="small" weight="bold" typographyProps={{ style: { color: portexColor.blue500 } }}>
                        {t("common:pickup")}
                      </Text>
                      {quoteRequest.stops?.[0]?.address ? (
                        <>
                          <Text size="small" weight="bold">
                            {quoteRequest.stops[0].address?.airport_name ??
                              quoteRequest.stops[0].address?.airport_iata_code ??
                              formatAddress.fromSnakeCase(quoteRequest.stops[0]?.address, "short")}
                          </Text>
                          <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                            {formatAddress.fromSnakeCase(quoteRequest.stops[0].address, "long")}
                          </Text>
                          {quoteRequest.stops[0].start && (
                            <div className="flex items-center">
                              <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {DateTime.fromISO(quoteRequest.stops[0].start).toFormat("cccc, MMM d")}
                                {quoteRequest.stops[0].start !== quoteRequest.stops[0].end && (
                                  <span>
                                    {" at "}
                                    {DateTime.fromISO(quoteRequest.stops[0].start).toFormat("h:mm a")}
                                    {quoteRequest.stops[0].end && (
                                      <span>
                                        {" - "}
                                        {DateTime.fromISO(quoteRequest.stops[0].end).toFormat("h:mm a")}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </Text>
                            </div>
                          )}
                        </>
                      ) : (
                        <Text size="small" weight="bold">
                          {EM_DASH}
                        </Text>
                      )}
                    </div>
                    <div className="flex flex-col space-y-3">
                      <Text size="small" weight="bold" typographyProps={{ style: { color: portexColor.green500 } }}>
                        {t("common:delivery")}
                      </Text>
                      {quoteRequest.stops?.[1]?.address ? (
                        <>
                          <Text size="small" weight="bold">
                            {quoteRequest.stops[1].address?.airport_name ??
                              quoteRequest.stops[1].address?.airport_iata_code ??
                              formatAddress.fromSnakeCase(quoteRequest.stops[1]?.address, "short")}
                          </Text>
                          <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                            {formatAddress.fromSnakeCase(quoteRequest.stops[1].address, "long")}
                          </Text>
                          {quoteRequest.stops[1].start && (
                            <div className="flex items-center">
                              <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {DateTime.fromISO(quoteRequest.stops[1].start).toFormat("cccc, MMM d")}
                                {quoteRequest.stops[1].start !== quoteRequest.stops[1].end && (
                                  <span>
                                    {" at "}
                                    {DateTime.fromISO(quoteRequest.stops[1].start).toFormat("h:mm a")}
                                    {quoteRequest.stops[1].end && (
                                      <span>
                                        {" - "}
                                        {DateTime.fromISO(quoteRequest.stops[1].end).toFormat("h:mm a")}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </Text>
                            </div>
                          )}
                        </>
                      ) : (
                        <Text size="small" weight="bold">
                          {EM_DASH}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </Paper>
            </Paper>
          </div>
        </div>
      </div>
    </StepCollapse>
  );
};
